import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import './Unsubscribe.css';

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [emailType, setEmailType] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const type = searchParams.get('type');

    if (!token || !type) {
      setStatus('error');
      setMessage('Invalid unsubscribe link. Please contact support if you need assistance.');
      return;
    }

    // Call unsubscribe API
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    axios
      .post(`${apiUrl}/api/email/unsubscribe?token=${token}&email_type=${type}`)
      .then((response) => {
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email);
        setEmailType(response.data.unsubscribed_from);
      })
      .catch((error) => {
        setStatus('error');
        const errorMessage =
          error.response?.data?.detail ||
          'Failed to process your unsubscribe request. Please try again or contact support.';
        setMessage(errorMessage);
      });
  }, [searchParams]);

  const getEmailTypeLabel = (type) => {
    switch (type) {
      case 'weekly_reports':
        return 'Weekly Progress Reports';
      case 'churn_alerts':
        return 'Re-engagement Reminders';
      case 'all':
        return 'All Automated Emails';
      default:
        return 'Email Notifications';
    }
  };

  return (
    <div className="unsubscribe-page">
      <div className="unsubscribe-container">
        {status === 'loading' && (
          <div className="unsubscribe-loading">
            <div className="spinner"></div>
            <h1>Processing your request...</h1>
            <p>Please wait while we update your email preferences.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="unsubscribe-success">
            <div className="icon-circle success-icon">✓</div>
            <h1>You've Been Unsubscribed</h1>
            <p className="success-message">{message}</p>

            <div className="details-box">
              <p className="detail-item">
                <strong>Email:</strong> {email}
              </p>
              <p className="detail-item">
                <strong>Unsubscribed from:</strong> {getEmailTypeLabel(emailType)}
              </p>
            </div>

            <div className="info-box">
              <p>
                <strong>What happens now?</strong>
              </p>
              <ul>
                <li>You will no longer receive {getEmailTypeLabel(emailType).toLowerCase()}</li>
                <li>This change is effective immediately</li>
                <li>You can resubscribe anytime from your account settings</li>
                <li>
                  <strong>Important:</strong> Safety alerts cannot be unsubscribed from
                  as they are required for your child's protection
                </li>
              </ul>
            </div>

            <div className="actions">
              <Link to="/dashboard" className="btn btn-primary">
                Go to Dashboard
              </Link>
              <Link to="/dashboard/settings" className="btn btn-secondary">
                Manage Email Preferences
              </Link>
            </div>

            <p className="support-text">
              Changed your mind?{' '}
              <Link to="/dashboard/settings" className="link">
                Resubscribe in your settings
              </Link>
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="unsubscribe-error">
            <div className="icon-circle error-icon">✕</div>
            <h1>Unsubscribe Failed</h1>
            <p className="error-message">{message}</p>

            <div className="error-details">
              <p>
                <strong>Common reasons for this error:</strong>
              </p>
              <ul>
                <li>The unsubscribe link has expired (valid for 1 year)</li>
                <li>The link was incomplete or corrupted</li>
                <li>You've already unsubscribed from these emails</li>
              </ul>
            </div>

            <div className="actions">
              <Link to="/dashboard/settings" className="btn btn-primary">
                Manage Preferences in Settings
              </Link>
              <a
                href="mailto:support@autismcompanion.com"
                className="btn btn-secondary"
              >
                Contact Support
              </a>
            </div>
          </div>
        )}

        <footer className="unsubscribe-footer">
          <p>
            © 2026 Autism Companion. All rights reserved. |{' '}
            <a href="mailto:support@autismcompanion.com">support@autismcompanion.com</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Unsubscribe;
